import {
  USER_TYPE_ADMIN,
  USER_TYPE_MANAGER,
  USER_TYPE_PRACTITIONER_LICENCE_OWNER,
  USER_TYPE_PRACTITIONER,
  USER_TYPE_RECEIPTS,
  USER_TYPE_SUPERADMIN
} from '../constants/userConstants'

const STORAGE_PREFIX = 'siteScope'
const ACTIVE_SCOPE_KEY = `${STORAGE_PREFIX}:active`

export const getScopeStorageKey = (userId) => `${STORAGE_PREFIX}:${String(userId)}`

export const readScopeByUserId = (userId) => {
  if (typeof window === 'undefined' || !userId) return null
  try {
    const raw = window.localStorage.getItem(getScopeStorageKey(userId))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const writeScopeByUserId = (userId, scope) => {
  if (typeof window === 'undefined' || !userId) return
  try {
    window.localStorage.setItem(getScopeStorageKey(userId), JSON.stringify(scope || {}))
  } catch {
    // ignore
  }
}

export const setActiveScope = (scope) => {
  if (typeof window === 'undefined') return
  try {
    if (!scope) {
      window.localStorage.removeItem(ACTIVE_SCOPE_KEY)
      return
    }
    window.localStorage.setItem(ACTIVE_SCOPE_KEY, JSON.stringify(scope))
  } catch {
    // ignore
  }
}

export const getActiveScope = () => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(ACTIVE_SCOPE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const normalizeAllowedSiteIds = (allowedSiteIds) => {
  if (!Array.isArray(allowedSiteIds)) return []
  const parsed = allowedSiteIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
  return Array.from(new Set(parsed))
}

export const normalizeScope = (scope = {}) => {
  const allowedSiteIds = normalizeAllowedSiteIds(scope.allowedSiteIds)
  const mode = scope.mode === 'single' ? 'single' : 'mixed'
  const rawSelectedSiteId =
    scope.selectedSiteId == null || scope.selectedSiteId === ''
      ? null
      : Number(scope.selectedSiteId)
  const selectedSiteId =
    Number.isFinite(rawSelectedSiteId) && rawSelectedSiteId > 0
      ? rawSelectedSiteId
      : null

  if (allowedSiteIds.length === 1) {
    return {
      mode: 'single',
      selectedSiteId: allowedSiteIds[0],
      allowedSiteIds
    }
  }

  if (mode === 'single') {
    const selectedAllowed = selectedSiteId && allowedSiteIds.includes(selectedSiteId)
    if (!selectedAllowed) {
      return {
        mode: 'mixed',
        selectedSiteId: null,
        allowedSiteIds
      }
    }
    return {
      mode: 'single',
      selectedSiteId,
      allowedSiteIds
    }
  }

  return { mode: 'mixed', selectedSiteId: null, allowedSiteIds }
}

export const isStaffType = (typeId) =>
  [USER_TYPE_PRACTITIONER, USER_TYPE_PRACTITIONER_LICENCE_OWNER, USER_TYPE_RECEIPTS, USER_TYPE_MANAGER].includes(Number(typeId))

export const isAdminType = (typeId) =>
  [USER_TYPE_ADMIN, USER_TYPE_SUPERADMIN].includes(Number(typeId))

export const getScopeQuery = (scope = getActiveScope()) => {
  const normalized = normalizeScope(scope || {})
  if (normalized.mode === 'single' && normalized.selectedSiteId) {
    return { siteMode: 'single', siteId: normalized.selectedSiteId }
  }
  return { siteMode: 'mixed' }
}

export const validateScopeBeforeRequest = (scope = getActiveScope()) => {
  const normalized = normalizeScope(scope || {})
  const typeId = Number(scope?.typeId || 0)

  if (normalized.mode === 'single' && !normalized.selectedSiteId) {
    return {
      valid: false,
      message: 'Please select a site'
    }
  }

  if (
    isStaffType(typeId) &&
    normalized.mode === 'single' &&
    normalized.selectedSiteId &&
    normalized.allowedSiteIds.length > 0 &&
    !normalized.allowedSiteIds.includes(Number(normalized.selectedSiteId))
  ) {
    return {
      valid: false,
      message: 'No access to selected site'
    }
  }

  return { valid: true, message: '' }
}

export const showScopeValidationError = (message) => {
  if (typeof window === 'undefined') return
  window.alert(message || 'Invalid clinic scope.')
}

export const filterAppointmentsByScope = (items = [], scope = getActiveScope()) => {
  const list = Array.isArray(items) ? items : []
  const normalized = normalizeScope(scope || {})
  const typeId = Number(scope?.typeId || 0)

  if (normalized.mode === 'single' && normalized.selectedSiteId) {
    const selected = Number(normalized.selectedSiteId)
    return list.filter((item) => Number(item?.siteId) === selected)
  }

  if (isStaffType(typeId) && normalized.allowedSiteIds.length > 0) {
    const allowedSet = new Set(normalized.allowedSiteIds.map((id) => Number(id)))
    return list.filter((item) => allowedSet.has(Number(item?.siteId)))
  }

  if (isAdminType(typeId)) {
    return list
  }

  return list
}

export const validateCreateSiteSelection = ({
  mode,
  selectedSiteId,
  allowedSiteIds,
  payloadSiteId
}) => {
  const allowed = normalizeAllowedSiteIds(allowedSiteIds)
  const selectedScopeSite = selectedSiteId == null ? null : Number(selectedSiteId)
  const payloadSite = payloadSiteId == null || payloadSiteId === '' ? null : Number(payloadSiteId)

  if (mode === 'single') {
    const fallbackSingleSite =
      (Number.isFinite(selectedScopeSite) && selectedScopeSite > 0 ? selectedScopeSite : null) ||
      (allowed.length === 1 ? allowed[0] : null) ||
      (Number.isFinite(payloadSite) && payloadSite > 0 ? payloadSite : null)

    if (!Number.isFinite(fallbackSingleSite) || fallbackSingleSite <= 0) {
      return { ok: false, message: 'Please select a site', resolvedSiteId: null }
    }
    if (allowed.length > 0 && !allowed.includes(fallbackSingleSite)) {
      return { ok: false, message: 'No access to selected site', resolvedSiteId: null }
    }
    return { ok: true, resolvedSiteId: fallbackSingleSite }
  }

  if (allowed.length === 1) {
    return { ok: true, resolvedSiteId: allowed[0] }
  }

  if (allowed.length > 1) {
    if (!Number.isFinite(payloadSite) || payloadSite <= 0) {
      return { ok: false, message: 'Please select a site', resolvedSiteId: null }
    }
    if (!allowed.includes(payloadSite)) {
      return { ok: false, message: 'No access to selected site', resolvedSiteId: null }
    }
    return { ok: true, resolvedSiteId: payloadSite }
  }

  return { ok: true, resolvedSiteId: payloadSite }
}
